import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { removeFilters, setFilters } from '@/app/common/reducers/filterSlice';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { convertToQueryString } from '@/utils/queryString';
import SelectFilterColumn from './SelectFilterColumn';
import AdvancedFilters2 from './AdvancedFilters2';
import Select from 'react-select';
import { useLocation, useSearchParams } from 'react-router-dom';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import type {
  AdvancedFiltersState,
  ColumnSelectChangeHandler,
  ColumnSelectOption,
  ColumnSelectOptionProps,
  FiltersComponentProps,
  FiltersRootState
} from './filterTypes';

const OptionLayout = (props: ColumnSelectOptionProps) => {
  const { innerProps, innerRef } = props;

  return (
    <>
      <style>{`
      .customized-searchbox-option:hover{
        background-color:var(--falcon-gray-link-hover-300) !important;
      }
      .customized-searchbox-option{
        background-color:var(--falcon-tertiary-bg) !important;
      }

      `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          backgroundColor: props.isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className="p-2 cursor-pointer border border-1 customized-searchbox-option"
      >
        <div className="text-800  fs--2">{props.data.label}</div>
      </div>
    </>
  );
};

const FiltersComponent = ({
  preQueries = '',
  columns,
  selectItems,
  EndComponent = <></>,
  SampleFilters = <></>,
  placement = 'auto',
  groupingMode,
  onGroupingModeChange,
  ipPrefixLength,
  onIpPrefixLengthChange
}: FiltersComponentProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFiltersState>(
    {}
  );
  const [filterValue, setFilterValue] = useState('');
  const [filteredData, setFilteredData] = useState<boolean | undefined>();
  const filters = useSelector((state: FiltersRootState) => state.filters);

  const resetAllFilters = () => {
    const nextParams = new URLSearchParams(searchParams);
    Array.from(nextParams.keys()).forEach(key => {
      if (key.startsWith('FBy')) {
        nextParams.delete(key);
      }
    });
    setSearchParams(nextParams);
    dispatch(removeFilters());
    setAdvancedFilters({});
    setFilterValue('');
    setFilteredData(undefined);
  };

  useEffect(() => {
    if (Object.keys(filters).length !== 0) {
      setAdvancedFilters(filters);
    } else {
      setAdvancedFilters({});
    }

    setFilteredData(undefined);
  }, [searchParams]);

  useEffect(() => {
    if (filteredData !== undefined) {
      setFilteredData(true);
    }

    if (Object.keys(filters).length === 0) {
      setAdvancedFilters({});
    }
  }, [filters]);

  const filterdColumns = columns.filter(
    item => item.type === 'string' && item.searchable === true
  );

  const [selectedColumn, setSelectedColumn] = useState<ColumnSelectOption>({
    value:
      filterdColumns.length !== 0 ? (filterdColumns[0].field ?? null) : null,
    label:
      filterdColumns.length !== 0 ? (filterdColumns[0].title ?? null) : null
  });
  const handleSelectedColumnChange: ColumnSelectChangeHandler = value => {
    setSelectedColumn(value ?? { value: null, label: null });
  };

  const updateFilters = () => {
    if (!selectedColumn.value) {
      return;
    }

    setAdvancedFilters(prev => {
      const updatedFilters = _.cloneDeep(prev);

      if (updatedFilters[selectedColumn.value!]) {
        const exists = updatedFilters[selectedColumn.value!].filterModes.some(
          mode => mode.value === filterValue
        );

        if (!exists) {
          updatedFilters[selectedColumn.value!].filterModes.push({
            mode: 'contains',
            value: filterValue,
            key: updatedFilters[selectedColumn.value!].filterModes.length + 1
          });
          updatedFilters[selectedColumn.value!].operator = 'and';
        }
      } else {
        updatedFilters[selectedColumn.value!] = {
          filterModes: [
            {
              mode: 'contains',
              value: filterValue,
              key: 1
            }
          ],
          operator: 'and',
          title: ''
        };
      }

      return updatedFilters;
    });
    setFilteredData(false);
  };

  const isLgUp = useMediaQuery(breakpointsMediaQuery.lg);

  return (
    filterdColumns.length !== 0 && (
      <Card className="mb-3 fs--2 fs-lg-0">
        <Card.Body className="align-items-center">
          {isLgUp ? (
            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div
                  style={{
                    maxWidth: 600,
                    zIndex: 3
                  }}
                  className="input-group position-relative"
                >
                  <div>
                    <Select<ColumnSelectOption, false>
                      components={{ Option: OptionLayout }}
                      menuPortalTarget={
                        typeof document !== 'undefined' ? document.body : null
                      }
                      menuPosition="fixed"
                      styles={{
                        menuPortal: baseStyles => ({
                          ...baseStyles,
                          zIndex: 1060
                        }),
                        control: baseStyles => ({
                          ...baseStyles,
                          paddingLeft: '0px',
                          paddingRight: '10px',
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important',
                          width: isLgUp ? '200px !important' : '100%',
                          cursor: 'pointer',
                          minHeight: '30px !important',
                          borderRadius: ' 0px 5px 5px 0px !important'
                        }),
                        input: baseStyles => ({
                          ...baseStyles,
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important'
                        })
                      }}
                      closeMenuOnSelect
                      options={filterdColumns.map(item => ({
                        value: item.field ?? null,
                        label: item.title ?? null
                      }))}
                      placeholder="انتخاب ستون"
                      classNamePrefix="react-select"
                      noOptionsMessage={() => 'موردی یافت نشد!!!'}
                      value={selectedColumn}
                      onChange={handleSelectedColumnChange}
                    />
                  </div>
                  <Form.Control
                    style={{ zIndex: 3 }}
                    type="text"
                    placeholder="جستجو در ستون انتخابی"
                    onChange={e => setFilterValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (filterValue.length !== 0) {
                          setFilterValue('');
                          updateFilters();
                        }
                      }
                    }}
                    name="start_url"
                    value={filterValue}
                    size={isLgUp ? undefined : 'sm'}
                  />
                  <ChildrensTooltip
                    placement="top"
                    html={
                      filterValue.length === 0 && <>مقدار جستجو را وارد کنید</>
                    }
                  >
                    <Button
                      className="border-300"
                      style={{
                        borderRadius: '5px 0px 0px 5px',
                        height: '100%'
                      }}
                      onClick={() => {
                        if (filterValue.length !== 0) {
                          setFilterValue('');
                          updateFilters();
                        }
                      }}
                      variant="outline-secondary"
                      title="جستجو"
                    >
                      <FontAwesomeIcon icon="search" />
                    </Button>
                  </ChildrensTooltip>
                </div>
                {SampleFilters}
                <SelectFilterColumn
                  columns={columns.filter(column => !column.noFilter)}
                  advancedFilters={advancedFilters}
                  setAdvancedFilters={setAdvancedFilters}
                />
                {onGroupingModeChange && (
                  <BacklinkGroupingSelect
                    value={groupingMode}
                    onChange={value => onGroupingModeChange(value)}
                  />
                )}
                {EndComponent}
                {onIpPrefixLengthChange && (
                  <ReferringIpPrefixSelect
                    value={ipPrefixLength}
                    onChange={value => onIpPrefixLengthChange(value)}
                  />
                )}
              </div>

              {Object.keys(advancedFilters).length > 0 && (
                <Row className="gap-3 my-3 gap-xxl-0">
                  <Col
                    className="d-flex gap-2 align-items-center flex-wrap"
                    md={12}
                    xxl={12}
                  >
                    <>
                      {Object.keys(advancedFilters).map(key => {
                        const column = columns.find(c => c.field === key);

                        return column ? (
                          <AdvancedFilters2
                            placement={placement}
                            columnKey={key}
                            key={key}
                            columnValue={column}
                            advancedFilters={advancedFilters}
                            setAdvancedFilters={setAdvancedFilters}
                            setFilteredData={setFilteredData}
                            selectItems={selectItems}
                            SampleFilters={SampleFilters}
                          />
                        ) : null;
                      })}
                      {location.search.includes('FBy') && (
                        <Button
                          className="p-1 fs--1 gap-2 d-flex align-items-center justify-content-center px-2"
                          variant="outline-secondary"
                          onClick={resetAllFilters}
                        >
                          <FontAwesomeIcon icon="undo-alt" />
                          <span>
                            ریست
                            <span onClick={e => e.stopPropagation()}></span>
                          </span>
                        </Button>
                      )}
                    </>
                  </Col>
                </Row>
              )}
              <div className="d-flex mt-2 gap-2">
                {(filteredData === false ||
                  (location.search.includes('FBy') &&
                    !_.isEqual(
                      decodeURIComponent(
                        location.search.replace('?', '')
                      ).substring(
                        location.search.indexOf('FBy') !== -1
                          ? location.search.indexOf('FBy') - 1
                          : 0
                      ),
                      decodeURIComponent(convertToQueryString(advancedFilters))
                    ))) && (
                  <Button
                    size="sm"
                    variant="falcon-primary"
                    onClick={() => {
                      let queryString = convertToQueryString(advancedFilters);
                      queryString = `${preQueries}&${queryString}`;
                      setSearchParams(queryString);
                      dispatch(setFilters(advancedFilters));
                    }}
                  >
                    اعمال تغییرات فیلتر
                  </Button>
                )}
                {filteredData === false && (
                  <Button
                    onClick={() => {
                      setAdvancedFilters({});
                      setFilteredData(true);
                      setFilterValue('');
                    }}
                    size="sm"
                    variant="outline-secondary"
                  >
                    لغو
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="d-flex gap-2 align-items-center gap-2 flex-wrap">
                <div
                  style={{
                    minWidth: '100%',
                    zIndex: 3,
                    width: 'unset !important'
                  }}
                  className="position-relative"
                >
                  <div className="mb-3">
                    <Select<ColumnSelectOption, false>
                      components={{ Option: OptionLayout }}
                      menuPortalTarget={
                        typeof document !== 'undefined' ? document.body : null
                      }
                      menuPosition="fixed"
                      styles={{
                        menuPortal: baseStyles => ({
                          ...baseStyles,
                          zIndex: 1060
                        }),
                        control: baseStyles => ({
                          ...baseStyles,
                          paddingLeft: '0px',
                          paddingRight: '10px',
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important',
                          width: '100%',
                          cursor: 'pointer',
                          minHeight: '30px !important',
                          borderRadius: '5px !important'
                        }),
                        input: baseStyles => ({
                          ...baseStyles,
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important'
                        })
                      }}
                      closeMenuOnSelect
                      options={filterdColumns.map(item => ({
                        value: item.field ?? null,
                        label: item.title ?? null
                      }))}
                      placeholder="انتخاب ستون"
                      classNamePrefix="react-select"
                      noOptionsMessage={() => 'موردی یافت نشد!!!'}
                      value={selectedColumn}
                      onChange={handleSelectedColumnChange}
                    />
                  </div>
                  <div className="input-group w-100">
                    <Form.Control
                      style={{ zIndex: 3 }}
                      type="text"
                      placeholder="جستجو در ستون انتخابی"
                      onChange={e => setFilterValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (filterValue.length !== 0) {
                            setFilterValue('');
                            updateFilters();
                          }
                        }
                      }}
                      name="start_url"
                      value={filterValue}
                    />
                    <ChildrensTooltip
                      placement="top"
                      html={
                        filterValue.length === 0 && (
                          <>مقدار جستجو را وارد کنید</>
                        )
                      }
                    >
                      <Button
                        className="border-300"
                        style={{
                          borderRadius: '5px 0px 0px 5px',
                          height: '100%'
                        }}
                        onClick={() => {
                          if (filterValue.length !== 0) {
                            setFilterValue('');
                            updateFilters();
                          }
                        }}
                        variant="outline-secondary"
                        title="جستجو"
                      >
                        <FontAwesomeIcon icon="search" />
                      </Button>
                    </ChildrensTooltip>
                  </div>
                </div>
                {SampleFilters}
                <SelectFilterColumn
                  columns={columns.filter(column => !column.noFilter)}
                  advancedFilters={advancedFilters}
                  setAdvancedFilters={setAdvancedFilters}
                />
                {onGroupingModeChange && (
                  <BacklinkGroupingSelect
                    value={groupingMode}
                    onChange={value => onGroupingModeChange(value)}
                  />
                )}
                {EndComponent}
                {onIpPrefixLengthChange && (
                  <ReferringIpPrefixSelect
                    value={ipPrefixLength}
                    onChange={value => onIpPrefixLengthChange(value)}
                  />
                )}
              </div>
              {Object.keys(advancedFilters).length > 0 && (
                <Row className="gap-3 my-3 gap-xxl-0">
                  <Col
                    className="d-flex gap-2 align-items-center flex-wrap"
                    md={12}
                    xxl={12}
                  >
                    <>
                      {Object.keys(advancedFilters).map(key => {
                        const column = columns.find(c => c.field === key);

                        return column ? (
                          <AdvancedFilters2
                            placement={placement}
                            columnKey={key}
                            key={key}
                            columnValue={column}
                            advancedFilters={advancedFilters}
                            setAdvancedFilters={setAdvancedFilters}
                            setFilteredData={setFilteredData}
                            selectItems={selectItems}
                            SampleFilters={SampleFilters}
                          />
                        ) : null;
                      })}
                      {location.search.includes('FBy') && (
                        <Button
                          className="p-1 fs--1 gap-2 d-flex align-items-center justify-content-center px-2"
                          variant="outline-secondary"
                          onClick={resetAllFilters}
                        >
                          <FontAwesomeIcon icon="undo-alt" />
                          <span>
                            ریست
                            <span onClick={e => e.stopPropagation()}></span>
                          </span>
                        </Button>
                      )}
                    </>
                  </Col>
                </Row>
              )}
              <div className="d-flex mt-2 gap-2">
                {(filteredData === false ||
                  (location.search.includes('FBy') &&
                    !_.isEqual(
                      decodeURIComponent(
                        location.search.replace('?', '')
                      ).substring(
                        location.search.indexOf('FBy') !== -1
                          ? location.search.indexOf('FBy') - 1
                          : 0
                      ),
                      decodeURIComponent(convertToQueryString(advancedFilters))
                    ))) && (
                  <Button
                    size="sm"
                    variant="falcon-primary"
                    onClick={() => {
                      let queryString = convertToQueryString(advancedFilters);
                      queryString = `${preQueries}&${queryString}`;
                      setSearchParams(queryString);
                      dispatch(setFilters(advancedFilters));
                    }}
                  >
                    اعمال تغییرات فیلتر
                  </Button>
                )}
                {filteredData === false && (
                  <Button
                    onClick={() => {
                      setAdvancedFilters({});
                      setFilteredData(true);
                      setFilterValue('');
                    }}
                    size="sm"
                    variant="outline-secondary"
                  >
                    لغو
                  </Button>
                )}
              </div>
            </div>
          )}
          <style>
            {`
            .customized-option:hover{
              background-color:black!important;
            }
            .badge-subtle-secondary {
              line-height: normal;
              text-align:right;
              font-size: 13px;
              border: var(--falcon-border-width) solid var(--falcon-gray-300);
            }    
           `}
          </style>
        </Card.Body>
      </Card>
    )
  );
};

export default FiltersComponent;

const BacklinkGroupingSelect = ({
  value,
  onChange
}: {
  value?: string;
  onChange: (value: string) => void;
}) => (
  <Form.Select
    value={value}
    onChange={event => onChange(event.target.value)}
    aria-label="گروه‌بندی بک‌لینک"
    className="fs-0"
    style={{
      minWidth: 190,
      width: 'auto',
      height: 36,
      paddingTop: 0,
      paddingBottom: 0,
      marginRight: 'auto'
    }}
  >
    <option value="domain">یک لینک برای هر دامنه</option>
    <option value="all">همه بک‌لینک‌ها</option>
  </Form.Select>
);

const ReferringIpPrefixSelect = ({
  value,
  onChange
}: {
  value?: number;
  onChange: (value: number) => void;
}) => (
  <Form.Select
    size="sm"
    value={value}
    onChange={event => onChange(Number(event.target.value))}
    aria-label="گروه‌بندی subnet"
    className="fs-0"
    style={{
      minWidth: 160,
      width: 'auto',
      height: 36,
      paddingTop: 0,
      paddingBottom: 0,
      marginRight: 'auto'
    }}
  >
    <option value={24}>تجمیع /24</option>
    <option value={16}>تجمیع /16</option>
  </Form.Select>
);

export type {
  AdvancedFilterField,
  AdvancedFiltersState,
  ColumnSelectChangeHandler,
  ColumnSelectOption,
  FilterColumn,
  FilterColumnType,
  FilterMode,
  FilterModeName,
  FilterOperator,
  FilterSelectOption,
  FiltersComponentProps
} from './filterTypes';
